import {
  HHAlert,
  HHButton,
  HHIconButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import CloseIcon from '@mui/icons-material/Close';
import Stack from '@mui/material/Stack';
import { useEffect, useState } from 'react';
import {
  ContextPartnershipType,
  useBillingContext,
} from '../../context/billing.context';
import { useSnackbarContext } from '../../context/snackbar.context';
import { useClientInsurersByClient } from '../../hooks/client-insurer-hook';
import { useGetAllDiscountsPaginated } from '../../hooks/discount-hooks';
import { useBillingWriteAccess } from '../../hooks/use-billing-write-access';
import {
  useCreateDiscountMutation,
  useGetAllPackagesQuery,
  useGetContractsV2LazyQuery,
  useGetPartnershipsQuery,
  useUpdateDiscountMutation,
} from '../../types';
import { IDiscount, toScopeInputs } from '../../utils/discount-utils';
import {
  cleanTypeNameGraphql,
  GqlNetworkStatus,
} from '../../utils/graphql-utils';
import DiscountDrawer, { DiscountDrawerMode } from './discount-drawer';
import DiscountsList from './discounts-list';
import { DiscountForm } from './form/form';
import { ParsedDiscountValues } from './form/form-constants';

export interface DiscountComponentProps {
  discountsData?: IDiscount[];
  loading?: boolean;
  clientId: number;
  partnershipIds?: number[];
  usageStatus: GqlNetworkStatus;
  disableAddNew?: boolean;
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (newPage: number) => void;
  onPageSizeChange?: (newSize: number) => void;
}

const DiscountComponent = ({
  discountsData,
  loading = true,
  partnershipIds,
  clientId,
  usageStatus,
  disableAddNew = false,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: DiscountComponentProps): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const { addPartnerships } = useBillingContext();
  // ACCOUNTS-AND-PLAN-MIGRATION: clientId is client-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  const { data: allDiscounts } = useGetAllDiscountsPaginated({
    clientId,
    partnershipIds,
  });
  const [drawerMode, setDrawerMode] = useState<DiscountDrawerMode>(
    DiscountDrawerMode.None,
  );
  const [discountToEdit, setDiscountToEdit] = useState<IDiscount | null>(null);
  const { data: clientData } = useClientInsurersByClient(clientId);
  const [createDiscountMutation, { data }] = useCreateDiscountMutation();
  const { setSnackbarState } = useSnackbarContext();
  const [updateDiscountMutation, { data: updateData }] =
    useUpdateDiscountMutation();
  const [
    getContractsByClientId,
    { data: contractsData, loading: contractsLoading, error: contractsError },
  ] = useGetContractsV2LazyQuery();
  const { data: availablePackages } = useGetAllPackagesQuery();
  const {
    data: partnershipData,
    loading: partnershipLoading,
    error: partnershipError,
  } = useGetPartnershipsQuery({
    variables: { type: 'billing' },
  });

  const createMessage = `Discount ${
    data?.createDiscount ? 'created' : 'not created'
  } for ${clientData?.name} client`;
  const updateMessage = `Discount ${
    updateData?.updateDiscount ? 'updated' : 'not updated'
  } for ${clientData?.name} client`;

  useEffect(() => {
    if (clientData && clientData.clientsInsurers.length > 0) {
      const clientId = clientData.clientsInsurers[0].client.id;
      getContractsByClientId({
        variables: {
          clientIds: [clientId],
          includeVoided: false,
          page: 1,
          size: 100,
        },
      });
    }
  }, [clientData, getContractsByClientId]);

  useEffect(() => {
    if (data || updateData) {
      setSnackbarState(
        data?.createDiscount ? createMessage : updateMessage,
        'success',
        3000,
      );
    }
  }, [data, updateData, createMessage, updateMessage, setSnackbarState]);

  useEffect(() => {
    if (partnershipData?.getPartnerships) {
      const partnerships =
        partnershipData.getPartnerships.reduce<ContextPartnershipType>(
          (acc, partnership) => {
            acc[partnership.id] = partnership;
            return acc;
          },
          {},
        );
      addPartnerships(partnerships);
    }
  }, [partnershipData, addPartnerships]);

  useEffect(() => {
    if (contractsError && !contractsLoading) {
      setSnackbarState('Unable to load currency information', 'info', 3000);
    }
  }, [contractsError, contractsLoading, setSnackbarState]);

  useEffect(() => {
    if (partnershipError && !partnershipLoading) {
      setSnackbarState('Unable to load partnership information', 'info', 3000);
    }
  }, [partnershipError, partnershipLoading, setSnackbarState]);

  const cleanPackages = availablePackages
    ? cleanTypeNameGraphql(availablePackages?.getAllPackages)
    : [];

  const handleClose = (): void => {
    setDiscountToEdit(null);
    setDrawerMode(DiscountDrawerMode.None);
  };

  const handleNewDiscountClick = (): void => {
    setDiscountToEdit(null);
    setDrawerMode(DiscountDrawerMode.New);
  };

  const sendFormData = async (values: ParsedDiscountValues): Promise<void> => {
    const { scopes, ...rest } = values;
    const scopesInput = toScopeInputs(scopes);
    const discountInputBase = {
      ...rest,
      ...(scopesInput ? { scopes: scopesInput } : {}),
    };

    try {
      if (drawerMode === DiscountDrawerMode.New) {
        await createDiscountMutation({
          variables: {
            discountInput: discountInputBase,
          },
          refetchQueries: ['getDiscountsPaginated', 'getDiscountUsage'],
        });
      }
      if (drawerMode === DiscountDrawerMode.Edit && discountToEdit) {
        await updateDiscountMutation({
          variables: {
            discountInput: {
              ...discountInputBase,
              discountDefinitionId: discountToEdit.discountDefinitionId,
            },
          },
          refetchQueries: ['getDiscountsPaginated', 'getDiscountUsage'],
        });
      }
      handleClose();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Error saving discount';
      setSnackbarState(message, 'error', 5000);
    }
  };

  const handleEditDiscountClick = (discountDefinitionId: number): void => {
    const foundDiscount =
      discountsData?.find(
        d => d.discountDefinitionId === discountDefinitionId,
      ) ?? null;
    setDiscountToEdit(foundDiscount);
    setDrawerMode(DiscountDrawerMode.Edit);
  };

  return (
    <HHStack direction="column" spacing={4}>
      <DiscountDrawer drawerMode={drawerMode} onClose={handleClose}>
        <HHStack alignSelf="flex-end">
          <HHIconButton hhVariant="medium" onClick={handleClose}>
            <CloseIcon />
          </HHIconButton>
        </HHStack>
        <Stack padding={1}>
          <DiscountForm
            clientId={clientId}
            formTitle={`${DiscountDrawerMode[drawerMode]} Discount`}
            submitTitle="Submit Discount"
            onSubmitCallback={sendFormData}
            onCancelCallback={(): void => handleClose()}
            discountToEdit={discountToEdit}
            currentDiscounts={allDiscounts}
            availablePackages={cleanPackages}
          />
        </Stack>
      </DiscountDrawer>
      <HHStack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <HHTypography hhVariant="h2">Discounts</HHTypography>
        {disableAddNew || !canWrite ? null : (
          <HHButton
            data-testid="discount-add-new-button"
            hhVariant="variant-bypass"
            variant="contained"
            size="medium"
            color="primary"
            onClick={handleNewDiscountClick}
            endIcon={<span>+</span>}
          >
            Add New
          </HHButton>
        )}
      </HHStack>
      {usageStatus.error && (
        <HHAlert
          hhVariant="outlined"
          severity="error"
          children={
            <HHTypography hhVariant="body">
              There was an error loading discount usage information
            </HHTypography>
          }
        />
      )}
      <DiscountsList
        discountsData={discountsData}
        onEditDiscountClick={canWrite ? handleEditDiscountClick : undefined}
        loading={loading}
        availablePackages={cleanPackages}
        currency={contractsData?.getContractsV2?.items[0]?.currency || null}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </HHStack>
  );
};

export default DiscountComponent;
